// ── AssistantDock — Evo Brain, panel único montado en AppShell ──────────────
// Reemplaza los 9 *ClaudePanel (Lead/Project/Contact/Task/Report/Billing/
// Email/Home/Onboarding) que reimplementaban, cada uno, el mismo shell
// (bubbles, tarjeta de tool_result, indicadores de thinking/ejecutando,
// agrupado de propuestas). Ese ~90% común vive acá, una sola vez. El ~10% que
// sí varía por tipo (título, placeholder, link de la entidad para la
// ApprovalCard, contenido fijado arriba como los chips de leads o el toggle
// de perfil de contactos) lo sigue resolviendo cada página, vía
// useAssistantEntity — este componente solo lee lo que quedó activo.
//
// Si no hay ninguna entidad activa todavía (arranque en frío, antes de que
// cualquier página llame a useAssistantEntity), no renderiza nada — no debería
// pasar en la práctica: toda página autenticada registra 'home' como mínimo
// (DashboardPage) y el dock retiene el MÓDULO activo al navegar a páginas que
// no registran nada propio (ej. listas).
//
// Lo que ya no retiene, desde 2026-08-30, es la ENTIDAD: al salir del detalle
// de un proyecto, el par pasa a (project, null) — el asistente sigue siendo el
// del módulo, sin entidad implícita. Ver releaseEntity en AssistantContext:
// seguir apuntando al proyecto anterior desde la lista era el camino corto a
// aplicarle un cambio al proyecto equivocado.

import { useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Icon } from '../Icon.js';
import { Button } from '../ui/Button.js';
import { AssistantShell } from '../ui/AssistantShell.js';
import { ApprovalCard, type ApprovalProposalItem } from '../ui/ApprovalCard.js';
import { useActiveAssistant } from '../../contexts/AssistantContext.js';
import type { AssistantMessage } from '../../hooks/useAssistant.js';
import { alCopiarTextoEnriquecido } from '../../lib/textoEnriquecido.js';

// ── Agrupar mensajes tool_proposal consecutivos por turnGroupId ─────────────

type RenderItem =
  | { kind: 'message'; msg: AssistantMessage }
  | { kind: 'proposalGroup'; turnGroupId: string; items: ApprovalProposalItem[] };

function groupProposals(messages: AssistantMessage[]): RenderItem[] {
  const out: RenderItem[] = [];
  for (const msg of messages) {
    if (msg.role === 'tool_proposal' && msg.proposal) {
      const item: ApprovalProposalItem = {
        id: msg.id,
        taskId: msg.proposal.taskId,
        turnGroupId: msg.proposal.turnGroupId,
        toolName: msg.toolName ?? '',
        verb: msg.proposal.verb,
        sentence: msg.proposal.summary.sentence,
        changes: msg.proposal.summary.changes,
        irreversible: msg.proposal.summary.irreversible,
        warning: msg.proposal.summary.warning,
        createdAt: msg.createdAt,
        status: msg.proposal.status,
      };
      const last = out[out.length - 1];
      if (last?.kind === 'proposalGroup' && last.turnGroupId === item.turnGroupId) {
        last.items.push(item);
        continue;
      }
      out.push({ kind: 'proposalGroup', turnGroupId: item.turnGroupId, items: [item] });
      continue;
    }
    out.push({ kind: 'message', msg });
  }
  return out;
}

// ── Sub-components ────────────────────────────────────────────────────────────

function UserBubble({ content }: { content: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--evo-space-2)' }}>
      <div style={{
        maxWidth: '85%', background: 'var(--evo-cyan)', color: 'var(--evo-fg-on-accent)',
        borderRadius: '10px 10px 2px 10px', padding: '6px 10px', fontSize: 'var(--evo-fs-label)',
        fontFamily: 'var(--evo-font)', lineHeight: 1.45, wordBreak: 'break-word',
      }}>
        {content}
      </div>
    </div>
  );
}

function AssistantBubble({ content, render }: { content: string; render?: ((content: string) => React.ReactNode) | undefined }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 'var(--evo-space-2)' }}>
      <div style={{
        maxWidth: '90%', background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)',
        borderRadius: '2px 10px 10px 10px', padding: '6px 10px', fontSize: 'var(--evo-fs-label)',
        fontFamily: 'var(--evo-font)', lineHeight: 1.45, color: 'var(--evo-fg-1)', wordBreak: 'break-word',
      }}>
        <div className="assistant-markdown" onCopy={alCopiarTextoEnriquecido}>
          {render ? render(content) : <Markdown remarkPlugins={[remarkGfm]}>{content}</Markdown>}
        </div>
      </div>
    </div>
  );
}

function ToolResultCard({ msg, onUndo, isUndoing }: { msg: AssistantMessage; onUndo: (id: number) => void; isUndoing: boolean }) {
  const { t } = useTranslation();
  const isUndone = msg.isUndone === 1;

  return (
    <div style={{
      background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)', borderRadius: 'var(--evo-r-sm)',
      padding: '8px 10px', marginBottom: 'var(--evo-space-2)', display: 'flex', flexDirection: 'column', gap: '6px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--evo-space-2)' }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: '5px', fontSize: 'var(--evo-fs-caption)',
          fontFamily: 'var(--evo-font)', color: isUndone ? 'var(--evo-fg-4)' : 'var(--evo-fg-3)',
          letterSpacing: 'var(--evo-ls-caps)', textTransform: 'uppercase', textDecoration: isUndone ? 'line-through' : 'none',
        }}>
          <Icon name="bolt" size={10} />
          {msg.toolName ?? t('assistant.dock.tool', { defaultValue: 'Tool' })}
        </div>

        {msg.undoable && !isUndone && (
          <Button
            variant="ghost" tone="muted" size="xs" loading={isUndoing}
            icon={<Icon name="return" size={10} />}
            onClick={() => onUndo(msg.id)}
          >
            {isUndoing ? t('assistant.dock.undoing', { defaultValue: 'Deshaciendo…' }) : t('assistant.dock.undo', { defaultValue: 'Deshacer' })}
          </Button>
        )}

        {isUndone && (
          <span style={{ fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-4)' }}>
            {t('assistant.dock.undone', { defaultValue: 'Deshecho' })}
          </span>
        )}
      </div>

      {msg.humanSummary && (
        <div style={{
          fontSize: 'var(--evo-fs-label)', fontFamily: 'var(--evo-font)',
          color: isUndone ? 'var(--evo-fg-4)' : 'var(--evo-fg-2)', lineHeight: 1.45,
          textDecoration: isUndone ? 'line-through' : 'none',
        }}>
          {msg.humanSummary}
        </div>
      )}
    </div>
  );
}

function ThinkingIndicator() {
  const { t } = useTranslation();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 0', marginBottom: 'var(--evo-space-2)' }}>
      <span style={{ display: 'inline-flex', gap: '3px', alignItems: 'center' }}>
        {[0, 1, 2].map(i => (
          <span key={i} style={{
            width: '5px', height: '5px', borderRadius: '50%', background: 'var(--evo-fg-4)',
            animation: `assistant-dot 1.2s ease-in-out ${i * 0.2}s infinite`,
          }} />
        ))}
      </span>
      <span style={{ fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-3)' }}>
        {t('assistant.dock.thinking', { defaultValue: 'Pensando…' })}
      </span>
    </div>
  );
}

function ExecutingIndicator({ label }: { label: string }) {
  const { t } = useTranslation();
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 6px',
      background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)',
      borderRadius: 'var(--evo-r-sm)', marginBottom: 'var(--evo-space-2)',
    }}>
      <Icon name="bolt" size={11} />
      <span style={{
        fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-2)',
        flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {t('assistant.dock.executing', { defaultValue: 'Ejecutando: {{label}}…', label })}
      </span>
      <span style={{
        width: '10px', height: '10px', borderRadius: '50%', border: '2px solid var(--evo-stroke)',
        borderTopColor: 'var(--evo-accent)', animation: 'spin 0.6s linear infinite', flexShrink: 0,
      }} />
    </div>
  );
}

let keyframesInjected = false;
function injectKeyframes() {
  if (keyframesInjected || typeof document === 'undefined') return;
  keyframesInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    @keyframes assistant-dot {
      0%, 60%, 100% { opacity: 0.2; transform: scale(0.85); }
      30% { opacity: 1; transform: scale(1.15); }
    }
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(style);
}

// ── Component ─────────────────────────────────────────────────────────────────

export function AssistantDock() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  injectKeyframes();

  const activeEntry = useActiveAssistant();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const api = activeEntry?.api ?? null;
  const config = activeEntry?.config ?? null;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [api?.messages.length, api?.streaming.phase]);

  const isStreaming = api ? api.streaming.phase !== 'idle' : false;
  const visibleMessages = useMemo(() => api?.messages.filter(m => m.role !== 'tool_call') ?? [], [api?.messages]);
  const renderItems = useMemo(() => groupProposals(visibleMessages), [visibleMessages]);

  if (!api || !config) return null;

  const entity = config.entityLink ?? null;

  return (
    <AssistantShell
      muted={api.muted}
      onToggleMute={api.toggleMuted}
      isSpeaking={api.isSpeaking}
      onStopSpeaking={api.stopSpeaking}
      ttsBroken={api.ttsBroken}
      ttsErrorMessage={api.ttsErrorMessage}
      onReactivateTts={api.reactivateTts}
      voiceNote={api.voiceNote}
      onOpenHistory={() => navigate('/historial')}
      onOpenMemory={() => navigate('/memoria')}
      storageKey="evo-assistant-collapsed"
      title={config.title}
      pills={api.pills}
      onSendMessage={msg => { void api.sendMessage(msg, config.composeContext); }}
      isStreaming={isStreaming}
      {...(config.inputDisabled !== undefined ? { inputDisabled: config.inputDisabled } : {})}
      inputPlaceholder={config.placeholder}
      {...(config.chipPlaceholder !== undefined ? { chipPlaceholder: config.chipPlaceholder } : {})}
      topContent={config.topContent}
      toolsContent={config.toolsContent}
      {...(config.activeTab !== undefined ? { activeTab: config.activeTab } : {})}
      {...(config.onActiveTabChange !== undefined ? { onActiveTabChange: config.onActiveTabChange } : {})}
    >
      {api.error != null && (
        <div style={{
          background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)', borderRadius: 'var(--evo-r-sm)',
          padding: '6px 10px', marginBottom: 'var(--evo-space-3)', fontSize: 'var(--evo-fs-caption)',
          fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-2)',
        }}>
          {t('assistant.dock.errorLabel', { defaultValue: 'Error' })}: {api.error}
        </div>
      )}

      {/* El panel arranca limpio cada día (2026-08-31). Si hay conversación
          anterior se dice acá y se puede traer sin salir de la pantalla: que
          empiece en blanco no puede significar que lo de ayer se perdió. */}
      {api.hasOlderMessages && !api.showingFullHistory && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--evo-space-2)',
          marginBottom: 'var(--evo-space-3)',
        }}>
          <span style={{
            fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-4)', fontFamily: 'var(--evo-font)',
          }}>
            {t('assistant.dock.olderMessages')}
          </span>
          <Button variant="link" size="xs" onClick={() => { api.loadFullHistory(); }}>
            {t('assistant.dock.showOlder')}
          </Button>
        </div>
      )}

      {visibleMessages.length === 0 && api.streaming.phase === 'idle' && (
        <p style={{ fontSize: 'var(--evo-fs-label)', color: 'var(--evo-fg-4)', fontFamily: 'var(--evo-font)', margin: 0, lineHeight: 1.5 }}>
          {config.emptyHint}
        </p>
      )}

      {renderItems.map(item => {
        if (item.kind === 'proposalGroup') {
          const pendingIds = item.items.filter(p => p.status === 'pending').map(p => p.taskId);
          return (
            <div key={`group-${item.turnGroupId}`} style={{ marginBottom: 'var(--evo-space-2)' }}>
              <ApprovalCard
                proposals={item.items}
                entity={entity}
                onApprove={(id, remember) => { void api.approveProposal(id, remember); }}
                onReject={id => { void api.rejectProposal(id); }}
                onApproveAll={() => { void api.approveAllProposals(pendingIds); }}
                onRejectAll={() => { void api.rejectAllProposals(pendingIds); }}
                approvingId={api.approvingTaskId}
                rejectingId={api.rejectingTaskId}
                approvingAll={api.bulkApproving}
                rejectingAll={api.bulkRejecting}
              />
            </div>
          );
        }

        const msg = item.msg;
        if (msg.role === 'user') return <UserBubble key={msg.id} content={msg.content} />;
        if (msg.role === 'tool_result') {
          return <ToolResultCard key={msg.id} msg={msg} onUndo={api.undoMessage} isUndoing={api.isUndoing} />;
        }
        return <AssistantBubble key={msg.id} content={msg.content} render={config.renderAssistantContent} />;
      })}

      {api.streaming.phase === 'thinking' && <ThinkingIndicator />}
      {api.streaming.phase === 'tool_executing' && api.streaming.toolLabel != null && (
        <ExecutingIndicator
          label={api.streaming.toolName != null
            ? t(`assistant.pills.${api.streaming.toolName}`, { defaultValue: api.streaming.toolLabel })
            : api.streaming.toolLabel}
        />
      )}
      {api.streaming.phase === 'text' && api.streaming.textAccum && (
        <AssistantBubble content={api.streaming.textAccum} render={config.renderAssistantContent} />
      )}

      <div ref={messagesEndRef} />
    </AssistantShell>
  );
}
